/**
 * Tile Component
 * Feature tile with icon, title, description, and action link
 * Icons: a Rapid Fusion icon name (config/icons.js, drawn in plum), or for older pages
 * a website icon number (1-33) or a Font Awesome name.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { isRfIcon, rfIconStyle } from '../../config/icons';

/**
 * Renders the appropriate icon based on type
 * - Number (1-33): Custom SVG icon from /images/Rapid Fusion Website Icons/
 * - String: Font Awesome icon class
 */
const TileIcon = ({ icon }) => {
  if (isRfIcon(icon)) {
    return <span className="rf-icon" style={rfIconStyle(icon)} aria-hidden="true" />;
  }
  if (typeof icon === 'number') {
    return (
      <img 
        src={`/images/Rapid Fusion Website Icons/${icon}.svg`} 
        alt="" 
        className="rf-tile-svg-icon"
      />
    );
  }
  return <i className={`fas fa-${icon}`}></i>;
};

const Tile = ({
  icon,
  title,
  description,
  linkTo,
  linkText = 'Learn More',
  external = false,
  secondaryLinkTo,
  secondaryLinkText,
  secondaryExternal = false,
  secondaryNewTab = false,
  secondaryTarget,
  extraLinks = [],
  disabled = false,
  disabledText = 'Coming soon',
  className = '',
  stepNumber = null,
  ...props
}) => {
  const renderLink = (to, text, isExt, primary, linkOpts = {}) => {
    if (!to) return null;
    const offSite = isExt && !to.startsWith('/');
    const newTab = !!linkOpts.newTab || offSite;
    const target = linkOpts.target || (newTab ? '_blank' : undefined);
    const LinkComponent = isExt ? 'a' : Link;
    const linkProps = isExt
      ? {
          href: to,
          ...(target && { target }),
          ...(offSite && { rel: 'noopener noreferrer' })
        }
      : { to };
    return (
      <LinkComponent
        {...linkProps}
        className={primary ? 'rf-btn rf-btn-primary' : 'rf-btn rf-btn-secondary'}
      >
        {text}
      </LinkComponent>
    );
  };

  return (
    <div className={`rf-tile${disabled ? ' rf-tile-disabled' : ''}${className ? ` ${className}` : ''}`} {...props}>
      <div className="rf-tile-content">
        {icon && (
          <div className="rf-tile-icon">
            <TileIcon icon={icon} />
          </div>
        )}
        <h3 className="rf-tile-title">
          {stepNumber ? <span className="rf-tile-step">{stepNumber}</span> : null}
          <span>{title}</span>
        </h3>
        <p className="rf-tile-description">{description}</p>
      </div>
      {(disabled || linkTo || secondaryLinkTo || extraLinks.length > 0) && (
        <div className="rf-tile-footer">
          {disabled ? (
            <span className="rf-btn-disabled" aria-disabled="true">{disabledText}</span>
          ) : (
            <>
              {renderLink(linkTo, linkText, external, true)}
              {renderLink(secondaryLinkTo, secondaryLinkText || 'Open course', secondaryExternal, false, {
                newTab: secondaryNewTab,
                target: secondaryTarget
              })}
              {extraLinks.map((link, idx) => (
                <span key={link.to || idx}>
                  {renderLink(link.to, link.text, !!link.external, false, {
                    newTab: link.newTab,
                    target: link.target
                  })}
                </span>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Tile;
